import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "motion/react";
import { useOnboarding } from "./OnboardingProvider";
import { TourTooltip } from "./TourTooltip";
import { CelebrationOverlay } from "./CelebrationOverlay";

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8;

const pulseKeyframes = `
@keyframes onboarding-pulse {
  0%, 100% { box-shadow: 0 0 0 9999px rgba(0,0,0,0.5), 0 0 0 0px rgba(59,130,246,0.5); }
  50% { box-shadow: 0 0 0 9999px rgba(0,0,0,0.5), 0 0 12px 4px rgba(59,130,246,0.7); }
}
`;

export function SpotlightOverlay() {
  const { activeTour, currentStepIndex, skipTour, nextStep } = useOnboarding();
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const [targetEl, setTargetEl] = useState<Element | null>(null);
  const [celebration, setCelebration] = useState<"confetti" | "checkmark" | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const currentStep = activeTour?.steps[currentStepIndex] ?? null;

  // Find target element and measure it
  const measure = useCallback(() => {
    if (!currentStep) {
      setRect(null);
      setTargetEl(null);
      return;
    }
    const el = document.querySelector(currentStep.targetSelector);
    if (!el) {
      setRect(null);
      setTargetEl(null);
      return;
    }
    setTargetEl(el);
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top - PADDING,
      left: r.left - PADDING,
      width: r.width + PADDING * 2,
      height: r.height + PADDING * 2,
    });
  }, [currentStep]);

  // Measure on step change and on resize/scroll
  useEffect(() => {
    // Defer initial measure to avoid synchronous setState in effect body
    const frame = requestAnimationFrame(() => measure());
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [measure]);

  // Keyboard handler
  useEffect(() => {
    if (!activeTour) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        skipTour();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [activeTour, skipTour]);

  const handleActionCompleted = useCallback(() => {
    const step = activeTour?.steps[currentStepIndex];
    if (step?.celebration && step.celebration !== "none") {
      setCelebration(step.celebration);
    } else {
      nextStep();
    }
  }, [activeTour, currentStepIndex, nextStep]);

  const handleCelebrationComplete = useCallback(() => {
    setCelebration(null);
    nextStep();
  }, [nextStep]);

  if (!activeTour || !currentStep) return null;

  const shouldPulse = currentStep.highlightPulse && rect;

  return (
    <>
      {/* Inject pulse keyframes */}
      <style>{pulseKeyframes}</style>

      {/* Full-screen click barrier — blocks clicks from reaching dialog backdrop underneath */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[60]"
        data-onboarding-overlay
        aria-label="Guided tour"
        onClick={skipTour}
      >
        {/* Spotlight cutout — visual only, pointer-events-none */}
        <motion.div
          className="absolute pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={
            rect
              ? {
                  boxShadow: shouldPulse ? undefined : `0 0 0 9999px rgba(0, 0, 0, 0.5)`,
                  animation: shouldPulse ? "onboarding-pulse 2s ease-in-out infinite" : undefined,
                  position: "absolute",
                  borderRadius: 8,
                  top: rect.top,
                  left: rect.left,
                  width: rect.width,
                  height: rect.height,
                  transition: "top 0.3s ease, left 0.3s ease, width 0.3s ease, height 0.3s ease",
                }
              : {
                  background: "rgba(0, 0, 0, 0.5)",
                  inset: 0,
                  position: "absolute",
                }
          }
        />

        {/* Clickable pass-through over target for interactive "click" steps */}
        {currentStep.action === "click" && rect && (
          <div
            className="absolute"
            style={{
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
              zIndex: 61,
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              // Forward click to the actual target element beneath
              const target = currentStep.actionTarget
                ? document.querySelector(currentStep.actionTarget)
                : targetEl;
              if (target instanceof HTMLElement) {
                target.click();
              }
              handleActionCompleted();
            }}
          />
        )}
      </div>

      {/* Tooltip rendered via portal — above ALL stacking contexts including Radix Dialog */}
      {createPortal(
        <TourTooltip targetRect={rect} targetEl={targetEl} />,
        document.body
      )}

      {/* Celebration overlay */}
      {celebration && (
        <CelebrationOverlay
          type={celebration}
          onComplete={handleCelebrationComplete}
        />
      )}
    </>
  );
}
